import { useCallback } from 'react';

import { Box } from '@mui/material';

import Button from '@/components/Buttons/Button';
import { useChartContext } from '@/components/Chart/context/ChartContext';
import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';
import { DATAHUB_SPACING_LG } from '@/features/DataHub/constants/ui';
import { useExport } from '@/features/DataHub/modules/Export/context/ExportContext';
import { useAppDispatch } from '@/hooks/redux-hooks';
import { closeDrawer } from '@/slices/drawerSlice';

import type { ExportingMimeTypeValue } from 'highcharts';

const ImageFooterButtons = () => {
  const dispatch = useAppDispatch();
  const { capturePosthogEvent } = useExport();
  const { formatDateString } = useDateText();
  const { chartRef } = useChartContext();
  const { exportSettings, setExportSettings } = useExport();
  const getDateSuffix = useCallback(() => {
    const date = formatDateString(new Date().toJSON(), DateTextVariants.WITH_TIME);
    return date.split(' ').join('T');
  }, [formatDateString]);

  const onExport = useCallback(
    (type: ExportingMimeTypeValue) => {
      if (chartRef.current?.chart) {
        chartRef.current.chart.exportChart(
          { type, filename: `GX-Chart-${getDateSuffix()}` },
          chartRef.current.chart.options.exporting?.chartOptions ?? chartRef.current.chart.options
        );
      }
    },
    [chartRef, getDateSuffix]
  );

  const handleButtonClick = () => {
    if (exportSettings.exportSubType) {
      capturePosthogEvent();
      onExport(exportSettings.exportSubType as ExportingMimeTypeValue);
      setExportSettings(prevState => {
        return { ...prevState, exportSubType: undefined };
      });
    }
  };
  return (
    <Box
      sx={{
        minHeight: '96px',
        width: '100%',
        backgroundColor: 'background.main',
        borderTop: '1px solid',
        borderColor: 'info.dark',
        p: DATAHUB_SPACING_LG,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: DATAHUB_SPACING_LG,
        '& > *': {
          flex: 1
        }
      }}
    >
      <Button
        sx={{
          color: 'primary.main',
          borderColor: 'primary.main'
        }}
        variant='outlined'
        onClick={() => {
          dispatch(closeDrawer());
        }}
      >
        Cancel
      </Button>
      <Button
        key='download'
        variant='contained'
        onClick={() => {
          handleButtonClick();
        }}
      >
        Download
      </Button>
    </Box>
  );
};

export default ImageFooterButtons;
